import { clsx } from 'clsx';
import { forwardRef } from 'react';

type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'card';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      variant = 'text',
      width,
      height,
      animation = 'pulse',
      style,
      ...props
    },
    ref
  ) => {
    const variantStyles = {
      text: 'rounded',
      circular: 'rounded-full',
      rectangular: 'rounded-lg',
      card: 'rounded-xl',
    };

    const animationStyles = {
      pulse: 'animate-pulse',
      wave: 'animate-skeleton-wave',
      none: '',
    };

    const defaultHeights = {
      text: 'h-4',
      circular: 'h-12',
      rectangular: 'h-24',
      card: 'h-48',
    };

    const defaultWidths = {
      text: 'w-full',
      circular: 'w-12',
      rectangular: 'w-full',
      card: 'w-full',
    };

    const customStyles: React.CSSProperties = {
      ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
      ...style,
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'bg-slate-800',
          variantStyles[variant],
          animationStyles[animation],
          !width && defaultWidths[variant],
          !height && defaultHeights[variant],
          className
        )}
        style={Object.keys(customStyles).length > 0 ? customStyles : undefined}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

// Pre-built skeleton patterns for common use cases

interface TextSkeletonProps extends Omit<SkeletonProps, 'variant'> {
  lines?: number;
  lastLineWidth?: string | number;
}

export function TextSkeleton({
  lines = 1,
  lastLineWidth = '75%',
  className,
  ...props
}: TextSkeletonProps) {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={index === lines - 1 && lines > 1 ? lastLineWidth : undefined}
          {...props}
        />
      ))}
    </div>
  );
}

interface CardSkeletonProps {
  header?: boolean;
  content?: boolean;
  footer?: boolean;
  headerHeight?: number;
  contentLines?: number;
  className?: string;
  animation?: 'pulse' | 'wave' | 'none';
}

export function CardSkeleton({
  header = true,
  content = true,
  footer = false,
  contentLines = 3,
  className,
  animation = 'pulse',
}: CardSkeletonProps) {
  return (
    <div
      className={clsx(
        'bg-slate-900 border border-slate-800 rounded-xl overflow-hidden',
        className
      )}
    >
      {header && (
        <div className="px-6 py-4 border-b border-slate-800">
          <Skeleton variant="text" width="60%" height={24} animation={animation} />
          <Skeleton variant="text" width="40%" height={16} className="mt-2" animation={animation} />
        </div>
      )}
      {content && (
        <div className="p-6">
          <TextSkeleton lines={contentLines} animation={animation} />
        </div>
      )}
      {footer && (
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex gap-2">
          <Skeleton variant="text" width={80} height={32} animation={animation} />
          <Skeleton variant="text" width={80} height={32} animation={animation} />
        </div>
      )}
    </div>
  );
}

interface EventCardSkeletonProps {
  className?: string;
}

export function EventCardSkeleton({ className }: EventCardSkeletonProps) {
  return (
    <div
      className={clsx(
        'bg-slate-900 border border-slate-800 rounded-xl overflow-hidden h-full flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Skeleton variant="text" width={80} height={20} className="mb-2" />
            <Skeleton variant="text" width="90%" height={24} />
          </div>
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" width={14} height={14} />
            <Skeleton variant="text" width={120} height={16} />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" width={14} height={14} />
            <Skeleton variant="text" width={80} height={16} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        <TextSkeleton lines={3} lastLineWidth="60%" />
        <div className="mt-auto pt-4 flex gap-2">
          <Skeleton variant="text" width={100} height={32} />
          <Skeleton variant="text" width={80} height={32} />
        </div>
      </div>
    </div>
  );
}

interface PageSkeletonProps {
  header?: boolean;
  contentLines?: number;
  sections?: number;
}

export function PageSkeleton({
  header = true,
  contentLines = 5,
  sections = 1,
}: PageSkeletonProps) {
  return (
    <div className="py-12 bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {header && (
          <div className="mb-10">
            <Skeleton variant="text" width={300} height={40} className="mb-4" />
            <Skeleton variant="text" width="50%" height={24} />
          </div>
        )}
        {Array.from({ length: sections }).map((_, sectionIndex) => (
          <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-12' : ''}>
            <TextSkeleton lines={contentLines} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default Skeleton;
